import { createFileRoute, Link } from "@tanstack/react-router";
import { pickBookCover } from "#/lib/assets";
import { getLineItemImage } from "#/lib/medusa-types/commerce";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import {
  selectHasHydrated,
  useCart,
  useCartStore,
  useCartTotals,
  useFormatCartMoney,
  useRemoveCartLine,
  useUpdateCartLine,
} from "#/store/cart";
import {
  BookCover,
  PageHeader,
  PillBtn,
  Skeleton,
} from "../components/primitives.js";

export const Route = createFileRoute("/cart")({
  head: () => ({ meta: [{ title: "Cart" }] }),
  component: CartPage,
});

function CartPage() {
  const hasHydrated = useCartStore(selectHasHydrated);
  const { data: cart, isPending } = useCart();
  const totals = useCartTotals();
  const formatCartMoney = useFormatCartMoney();
  const updateLine = useUpdateCartLine();
  const removeLine = useRemoveCartLine();

  const items = cart?.items ?? [];
  const showSkeleton = !hasHydrated || isPending;

  return (
    <div data-screen-label="Cart">
      <PageHeader
        title="Your Cart"
        subtitle="Review your items and proceed to checkout."
      />
      <div className="mx-auto grid w-full max-w-figma grid-cols-1 lg:grid-cols-[1fr_400px] gap-12 px-6 md:px-10 py-12 md:py-16">
        <div>
          {showSkeleton ? (
            <div
              className="flex flex-col gap-4 py-6"
              aria-label="Loading cart"
              role="status"
            >
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="flex gap-6 border-lp-grey border-b-2 py-6"
                >
                  <Skeleton className="h-[140px] w-[100px] rounded-lg" />
                  <div className="flex-1 space-y-3">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center font-display text-lp-text-lt">
              Your cart is empty.{" "}
              <Link
                to={ROUTES.shop}
                className="text-lp-green-dk underline hover:text-lp-green-mid"
              >
                Browse the catalog
              </Link>
              .
            </div>
          ) : (
            items.map((it) => {
              const thumb = getLineItemImage(it, pickBookCover);
              const subtitle =
                it.product_collection ??
                it.variant?.product?.collection?.title ??
                "";
              const qty = Number(it.quantity);
              return (
                <div
                  key={it.id}
                  className="flex gap-6 border-lp-grey border-b-2 py-6"
                >
                  <BookCover src={thumb} w={100} h={140} fit="contain" />
                  <div className="flex-1">
                    {subtitle && (
                      <div className="font-display text-lp-green-mid text-sm">
                        {subtitle}
                      </div>
                    )}
                    <div className="mb-1 font-display font-semibold text-lg text-lp-text">
                      {it.title}
                    </div>
                    <div className="font-mono text-base text-lp-text">
                      {formatCartMoney(it.unit_price)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end justify-between">
                    <button
                      type="button"
                      onClick={() =>
                        removeLine.mutate(
                          { lineItemId: it.id },
                          {
                            onSuccess: () =>
                              notify.success("Removed from cart"),
                            onError: (e) =>
                              notify.error("Couldn't remove item", {
                                description:
                                  e instanceof Error ? e.message : undefined,
                              }),
                          },
                        )
                      }
                      className="cursor-pointer border-0 bg-transparent font-display text-lp-text-lt text-sm hover:text-lp-green-mid"
                    >
                      Remove
                    </button>
                    <div className="flex h-10 w-28 items-center rounded-full border-2 border-lp-green">
                      <button
                        type="button"
                        aria-label="Decrease"
                        onClick={() =>
                          updateLine.mutate({
                            lineItemId: it.id,
                            quantity: qty - 1,
                          })
                        }
                        className="h-full flex-1 cursor-pointer border-0 bg-transparent font-mono text-lp-green"
                      >
                        −
                      </button>
                      <span className="font-mono text-lp-green">{qty}</span>
                      <button
                        type="button"
                        aria-label="Increase"
                        onClick={() =>
                          updateLine.mutate({
                            lineItemId: it.id,
                            quantity: qty + 1,
                          })
                        }
                        className="h-full flex-1 cursor-pointer border-0 bg-transparent font-mono text-lp-green"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <aside className="h-fit rounded-3xl bg-lp-green-soft p-8 lg:sticky lg:top-32">
          <h3 className="m-0 mb-6 font-bold font-display text-2xl text-lp-green-dk">
            Order Summary
          </h3>
          <div className="mb-3 flex justify-between font-display text-base text-lp-text">
            <span>Subtotal</span>
            <span className="font-mono">
              {formatCartMoney(totals.subtotal)}
            </span>
          </div>
          <div className="mb-3 flex justify-between font-display text-base text-lp-text">
            <span>Shipping</span>
            <span className="font-mono">
              {totals.shippingTotal > 0
                ? formatCartMoney(totals.shippingTotal)
                : "—"}
            </span>
          </div>
          <div className="my-4 h-px bg-lp-green-mid" />
          <div className="mb-6 flex justify-between font-bold font-display text-lp-green-dk text-xl">
            <span>Total</span>
            <span className="font-mono">{formatCartMoney(totals.total)}</span>
          </div>
          <Link to={ROUTES.checkout}>
            <PillBtn variant="green" size="md" className="w-full">
              Checkout
            </PillBtn>
          </Link>
          <Link to={ROUTES.shop}>
            <PillBtn variant="white" size="sm" className="mt-3 w-full">
              Continue Shopping
            </PillBtn>
          </Link>
        </aside>
      </div>
    </div>
  );
}
